import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ScrollToTop } from './hooks/scroll-to-top';
import { NotFound } from './pages/error-page/not-found';
import { RedirectIfAuthenticated } from './routing/redirect-if-authenticated';
import { css, Global } from '@emotion/react';
import { Chat } from './pages/chat/chat';

import { OnboardingRouter } from './pages/onboarding/onboarding-router';
import { RegistrationShellWrapper } from './pages/registration/registration-shell-wrapper';
import { LoginShellWrapper } from './pages/login/login-shell-wrapper';
import { AuthedGate } from './routing/authed-gate';
import { AppProviders } from './app-providers';
import { ErrorBoundary } from './components/error-boundary';
import { usePwaInstall } from './hooks/use-pwa-install';
import { InstallWrapper } from './pages/registration/install-wrapper';
import { usePageView } from './hooks/use-page-view';
import { useDeepLinks } from './hooks/use-deep-links';
import { isNative, isIos, isAndroid } from './platform';
import { NativePermissionsGate } from './components/native-permissions-gate/native-permissions-gate';

const PageViewTracker = () => { usePageView(); return null; };
const DeepLinkHandler = () => { useDeepLinks(); return null; };

const BrowserGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isStandalone, isIos, isAndroid } = usePwaInstall();
  const [detectionReady, setDetectionReady] = React.useState(false);
  const isProd = import.meta.env.VITE_ENVIRONMENT === 'PROD' || import.meta.env.VITE_ENVIRONMENT === 'STAGING';

  React.useEffect(() => {
    if (isIos && isProd) {
      const timer = setTimeout(() => {
        setDetectionReady(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setDetectionReady(true);
      return undefined;
    }
  }, [isIos, isProd]);

  // Native Capacitor app — always bypass install gate
  if (isNative()) return <>{children}</>;

  if (!detectionReady) {
    return null;
  }

  if (isProd) {
    const isMobile = isIos || isAndroid;

    // Desktop browsers pass through; only block mobile browsers not in standalone/PWA mode
    if (isMobile && !isStandalone) {
      return <InstallWrapper />;
    }
  }

  return <>{children}</>;
};

export const App: React.FC = () => (
  <ErrorBoundary>
    <AppProviders>
      <ScrollToTop />
      <PageViewTracker />
      <DeepLinkHandler />
      <Global
        styles={css`
          html {
            margin: 0;
            padding: 0;
            overscroll-behavior: none;
            box-sizing: border-box;
            background-color: #1a1a1c;
            min-height: calc(100% + env(safe-area-inset-top, 0px));
          }

          body {
            background-color: #1a1a1c;
            margin: 0;
            padding: 0;
            min-height: 100%;
          }

          #root {
            min-height: 100vh;
            min-height: 100dvh;
            height: 100dvh;
            display: flex;
            flex-direction: column;
            background-color: #1a1a1c;
          }


          body.allow-scroll {
            touch-action: pan-x pan-y;
            overscroll-behavior: auto;
          }

          ${isIos() ? `
            html {
              height: 100% !important;
              min-height: 0 !important;
              overflow: hidden;
            }
            body {
              height: 100% !important;
              min-height: 0 !important;
              margin: 0;
              padding: 0;
              overflow: hidden;
              overscroll-behavior: none;
              position: relative;
              background-color: #1a1a1c !important;
            }
            #root {
              transform: translateZ(0);
              position: absolute !important;
              top: 0;
              left: 0;
              right: 0;
              bottom: var(--keyboard-height, 0px);
              height: auto !important;
              min-height: 0 !important;
              overflow: auto;
              overscroll-behavior: none;
              /* no CSS transition — JS in native-ui.ts animates --keyboard-height frame-by-frame */
            }
          ` : ''}

          ${isAndroid() ? `
            html {
              height: 100% !important;
              min-height: 0 !important;
              overflow: hidden;
            }
            body {
              height: 100% !important;
              min-height: 0 !important;
              margin: 0;
              padding: 0;
              overflow: hidden;
              overscroll-behavior: none;
              position: relative;
              background-color: #1a1a1c !important;
            }
            #root {
              position: fixed !important;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              height: auto !important;
              min-height: 0 !important;
              overflow: auto;
              overscroll-behavior: none;
            }
          ` : ''}


          @font-face {
            font-family: 'Custora';
            src:
              url('/fonts/Custora.woff2') format('woff2'),
              url('/fonts/Custora.woff') format('woff'),
              url('/fonts/Custora.ttf') format('truetype');
            font-weight: 300;
            font-style: normal;
            font-display: swap;
          }

          html {
            font-synthesis: none;
            font-synthesis-weight: none;
            font-synthesis-style: none;
            --sidebar-width: 0px;
          }
            
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
        `}
      />

      <NativePermissionsGate>
      <BrowserGate>
        <Routes>
          <Route
            path="/login"
            element={
              <RedirectIfAuthenticated>
                <LoginShellWrapper />
              </RedirectIfAuthenticated>
            }
          />

          <Route
            path="/"
            element={
              <RedirectIfAuthenticated>
                <RegistrationShellWrapper />
              </RedirectIfAuthenticated>
            }
          />

          <Route
            path="/onboarding/*"
            element={
              <AuthedGate>
                <OnboardingRouter />
              </AuthedGate>
            }
          />

          <Route
            path="/chat/*"
            element={
              <AuthedGate>
                <Chat />
              </AuthedGate>
            }
          />

          <Route
            path="/notifications"
            element={
              <AuthedGate>
                <Navigate to="/chat" replace />
              </AuthedGate>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserGate>
      </NativePermissionsGate>
    </AppProviders>
  </ErrorBoundary>
);
